import { ref } from 'vue'
import { 
  getFirestore, 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  orderBy, 
  Timestamp,
  limit as firestoreLimit
} from 'firebase/firestore'
import { useCompanyStore } from '../stores/company'
import { useAuthStore } from '../stores/auth'
import { useSystemFieldsConfig } from './useSystemFieldsConfig'

import { formatDate as coreFormatDate } from '@/composables/useFormat'
// Tipos de acción que se pueden registrar
export type POChangeAction =
  | 'CREATED'
  | 'CREATED_FROM_IMPORT'
  | 'FIELD_UPDATED'
  | 'ITEM_UPDATED'
  | 'ITEM_FIELD_UPDATED'
  | 'STATUS_CHANGED'
  | 'DOCUMENT_UPLOADED'
  | 'DOCUMENT_DELETED'
  | 'DOCUMENT_TYPE_CHANGED'
  | 'COMMENT_ADDED'
  | 'COMMENT_EDITED'
  | 'COMMENT_DELETED'
  | 'CONFIRMATION_APPLIED'
  | 'CONFIRMATION_REVERSED'
  | 'SHIPMENT_ASSOCIATED'
  | 'SHIPMENT_DISSOCIATED'
  | 'SHIPMENT_CREATED'
  | 'DELETED'

export interface POChangeLogEntry {
  id?: string
  action: POChangeAction
  timestamp: any
  userId: string
  userName: string
  description: string
  changes?: {
    field: string
    fieldLabel?: string
    oldValue: any
    newValue: any
  }[]
  metadata?: Record<string, any>
}

export function usePOChangeLog() {
  const companyStore = useCompanyStore()
  const authStore = useAuthStore()
  const db = getFirestore()
  const { getModuleFields } = useSystemFieldsConfig()

  const changeLogs = ref<POChangeLogEntry[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Resolver el label legible de un campo a partir de su name técnico
   */
  const resolveFieldLabel = (fieldName: string): string => {
    const fields = getModuleFields.value('purchaseOrders')
    const found = fields.find(f => f.name === fieldName || f.id === fieldName)
    return found?.label || fieldName
  }

  /**
   * Obtener datos del usuario actual
   */
  const getCurrentUser = () => {
    const user = authStore.user
    return {
      userId: user?.uid || 'SYSTEM',
      userName: user?.name 
        ? `${user.name}${user.lastName ? ' ' + user.lastName : ''}`.trim()
        : user?.email || 'Sistema'
    }
  }

  /**
   * Registrar un cambio en la colección changeLogs de la orden de compra
   */
  const logChange = async (
    purchaseOrderId: string,
    action: POChangeAction,
    description: string,
    changes?: POChangeLogEntry['changes'],
    metadata?: Record<string, any>
  ): Promise<void> => {
    try {
      const companyId = companyStore.selectedCompanyId
      if (!companyId || !purchaseOrderId) {
        console.warn('⚠️ No se puede registrar cambio: falta companyId o purchaseOrderId')
        return
      }

      const { userId, userName } = getCurrentUser()

      const logEntry: Omit<POChangeLogEntry, 'id'> = {
        action,
        timestamp: Timestamp.now(),
        userId,
        userName,
        description,
        ...(changes && changes.length > 0 ? { changes } : {}),
        ...(metadata ? { metadata } : {})
      }

      const changeLogsRef = collection(
        db, 
        `Companies/${companyId}/purchaseOrders/${purchaseOrderId}/changeLogs`
      )

      await addDoc(changeLogsRef, sanitizeForFirestore(logEntry))
    } catch (err) {
      console.error('❌ Error registrando cambio en changeLogs:', err)
    }
  }

  /**
   * Cargar historial de cambios de una orden de compra
   */
  const fetchChangeLogs = async (purchaseOrderId: string, maxResults?: number) => {
    try {
      isLoading.value = true
      error.value = null

      const companyId = companyStore.selectedCompanyId
      if (!companyId) throw new Error('No company selected')

      const changeLogsRef = collection(
        db, 
        `Companies/${companyId}/purchaseOrders/${purchaseOrderId}/changeLogs`
      )

      const constraints: any[] = [orderBy('timestamp', 'desc')]
      if (maxResults) {
        constraints.push(firestoreLimit(maxResults))
      }

      const q = query(changeLogsRef, ...constraints)
      const snapshot = await getDocs(q)

      changeLogs.value = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as POChangeLogEntry[]

    } catch (err) {
      console.error('Error cargando changeLogs:', err)
      error.value = err instanceof Error ? err.message : 'Error al cargar historial'
      changeLogs.value = []
    } finally {
      isLoading.value = false
    }
  }

  // ===================================
  // MÉTODOS HELPER PARA REGISTRAR CAMBIOS ESPECÍFICOS
  // ===================================

  /**
   * Registrar creación de orden de compra
   */
  const logCreation = async (purchaseOrderId: string, source: 'manual' | 'import' = 'manual') => {
    const action: POChangeAction = source === 'import' ? 'CREATED_FROM_IMPORT' : 'CREATED'
    const description = source === 'import' 
      ? 'Orden de compra creada desde importación de datos'
      : 'Orden de compra creada'
    await logChange(purchaseOrderId, action, description)
  }

  /**
   * Registrar actualización de campo
   */
  const logFieldUpdate = async (
    purchaseOrderId: string,
    field: string,
    fieldLabel: string,
    oldValue: any,
    newValue: any
  ) => {
    const resolvedLabel = resolveFieldLabel(field) || fieldLabel
    const formattedOld = formatValue(oldValue)
    const formattedNew = formatValue(newValue)
    const description = `Campo "${resolvedLabel}" actualizado de "${formattedOld}" a "${formattedNew}"`
    
    await logChange(purchaseOrderId, 'FIELD_UPDATED', description, [{
      field,
      fieldLabel: resolvedLabel,
      oldValue,
      newValue
    }])
  }

  /**
   * Registrar actualización de item
   */
  const logItemUpdate = async (
    purchaseOrderId: string,
    itemIdentifier: string,
    field: string,
    fieldLabel: string,
    oldValue: any,
    newValue: any
  ) => {
    const resolvedLabel = resolveFieldLabel(field) || fieldLabel
    const formattedOld = formatValue(oldValue)
    const formattedNew = formatValue(newValue)
    const description = `Item "${itemIdentifier}": campo "${resolvedLabel}" actualizado de "${formattedOld}" a "${formattedNew}"`
    
    await logChange(purchaseOrderId, 'ITEM_UPDATED', description, [{
      field,
      fieldLabel: resolvedLabel,
      oldValue,
      newValue
    }], { itemIdentifier })
  }

  /**
   * Registrar actualización de campo de item
   */
  const logItemFieldUpdate = async (
    purchaseOrderId: string,
    itemIdentifier: string,
    changes: { field: string; fieldLabel?: string; oldValue: any; newValue: any }[]
  ) => {
    const resolvedChanges = changes.map(c => ({
      field: c.field,
      fieldLabel: resolveFieldLabel(c.field) || c.fieldLabel || c.field,
      oldValue: c.oldValue,
      newValue: c.newValue
    }))

    const fieldsText = resolvedChanges.map(c => c.fieldLabel).join(', ')
    const description = resolvedChanges.length === 1
      ? `Item "${itemIdentifier}": campo "${resolvedChanges[0].fieldLabel}" actualizado de "${formatValue(resolvedChanges[0].oldValue)}" a "${formatValue(resolvedChanges[0].newValue)}"`
      : `Item "${itemIdentifier}": ${resolvedChanges.length} campos actualizados (${fieldsText})`

    await logChange(purchaseOrderId, 'ITEM_FIELD_UPDATED', description, resolvedChanges, { itemIdentifier })
  }

  /**
   * Registrar cambio de estado
   */
  const logStatusChange = async (
    purchaseOrderId: string,
    oldStatus: string,
    newStatus: string
  ) => {
    const description = `Estado cambiado de "${oldStatus || 'Sin estado'}" a "${newStatus}"`
    await logChange(purchaseOrderId, 'STATUS_CHANGED', description, [{
      field: 'status',
      fieldLabel: 'Estado',
      oldValue: oldStatus,
      newValue: newStatus
    }])
  }

  /**
   * Registrar subida de documento
   */
  const logDocumentUploaded = async (
    purchaseOrderId: string,
    fileName: string,
    documentType: string
  ) => {
    const description = `Documento "${fileName}" subido (tipo: ${documentType})`
    await logChange(purchaseOrderId, 'DOCUMENT_UPLOADED', description, undefined, {
      fileName,
      documentType
    })
  }

  /**
   * Registrar eliminación de documento
   */
  const logDocumentDeleted = async (
    purchaseOrderId: string,
    fileName: string
  ) => {
    const description = `Documento "${fileName}" eliminado`
    await logChange(purchaseOrderId, 'DOCUMENT_DELETED', description, undefined, {
      fileName
    })
  }

  /**
   * Registrar cambio de tipo de documento
   */
  const logDocumentTypeChanged = async (
    purchaseOrderId: string,
    fileName: string,
    oldType: string,
    newType: string
  ) => {
    const description = `Tipo del documento "${fileName}" cambiado de "${oldType}" a "${newType}"`
    await logChange(purchaseOrderId, 'DOCUMENT_TYPE_CHANGED', description, [{
      field: 'fileType',
      fieldLabel: 'Tipo de documento',
      oldValue: oldType,
      newValue: newType
    }], { fileName })
  }

  /**
   * Registrar comentario agregado
   */
  const logCommentAdded = async (purchaseOrderId: string, commentPreview: string) => {
    const preview = commentPreview.length > 80 
      ? commentPreview.substring(0, 80) + '...' 
      : commentPreview
    const description = `Comentario agregado: "${preview}"`
    await logChange(purchaseOrderId, 'COMMENT_ADDED', description, undefined, {
      comment: commentPreview
    })
  }

  /**
   * Registrar comentario editado
   */
  const logCommentEdited = async (
    purchaseOrderId: string, 
    oldContent: string, 
    newContent: string
  ) => {
    const description = `Comentario editado`
    await logChange(purchaseOrderId, 'COMMENT_EDITED', description, [{
      field: 'comment',
      fieldLabel: 'Comentario',
      oldValue: oldContent.length > 80 ? oldContent.substring(0, 80) + '...' : oldContent,
      newValue: newContent.length > 80 ? newContent.substring(0, 80) + '...' : newContent
    }])
  }

  /**
   * Registrar comentario eliminado
   */
  const logCommentDeleted = async (purchaseOrderId: string, commentPreview: string) => {
    const preview = commentPreview.length > 80 
      ? commentPreview.substring(0, 80) + '...' 
      : commentPreview
    const description = `Comentario eliminado: "${preview}"`
    await logChange(purchaseOrderId, 'COMMENT_DELETED', description, undefined, {
      comment: commentPreview
    })
  }

  /**
   * Registrar confirmación aplicada
   */
  const logConfirmationApplied = async (
    purchaseOrderId: string,
    details?: string
  ) => {
    const description = details || 'Confirmación aplicada a la orden de compra'
    await logChange(purchaseOrderId, 'CONFIRMATION_APPLIED', description)
  }

  /**
   * Registrar confirmación revertida
   */
  const logConfirmationReversed = async (
    purchaseOrderId: string,
    details?: string
  ) => {
    const description = details || 'Confirmación revertida en la orden de compra'
    await logChange(purchaseOrderId, 'CONFIRMATION_REVERSED', description)
  }

  /**
   * Registrar asociación con embarque
   */
  const logShipmentAssociated = async (
    purchaseOrderId: string,
    shipmentNumber: string
  ) => {
    const description = `Orden asociada al embarque "${shipmentNumber}"`
    await logChange(purchaseOrderId, 'SHIPMENT_ASSOCIATED', description, undefined, {
      shipmentNumber
    })
  }

  /**
   * Registrar creación de embarque desde la OC
   */
  const logShipmentCreated = async (
    purchaseOrderId: string,
    shipmentNumber: string
  ) => {
    const description = `Embarque "${shipmentNumber}" creado desde esta orden de compra`
    await logChange(purchaseOrderId, 'SHIPMENT_CREATED', description, undefined, {
      shipmentNumber
    })
  }

  // ===================================
  // HELPERS
  // ===================================

  /**
   * Sanitizar objeto para Firestore: reemplaza undefined por null recursivamente
   */
  const sanitizeForFirestore = (obj: any): any => {
    if (obj === undefined) return null
    if (obj === null || typeof obj !== 'object') return obj
    if (obj instanceof Date) return obj
    if (typeof obj.toDate === 'function') return obj // Firestore Timestamp
    if (Array.isArray(obj)) return obj.map(sanitizeForFirestore)
    
    const sanitized: Record<string, any> = {}
    for (const [key, value] of Object.entries(obj)) {
      sanitized[key] = sanitizeForFirestore(value)
    }
    return sanitized
  }

  const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === '') return 'vacío'
    if (typeof value === 'boolean') return value ? 'Sí' : 'No'
    if (value instanceof Date) return coreFormatDate(value)
    if (typeof value === 'object' && value.toDate) return coreFormatDate(value.toDate())
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return coreFormatDate(value.split('T')[0])
    }
    return String(value)
  }

  return {
    changeLogs,
    isLoading,
    error,
    logChange,
    fetchChangeLogs,
    logCreation,
    logFieldUpdate,
    logItemUpdate,
    logItemFieldUpdate,
    logStatusChange,
    logDocumentUploaded,
    logDocumentDeleted,
    logDocumentTypeChanged,
    logCommentAdded,
    logCommentEdited,
    logCommentDeleted,
    logConfirmationApplied,
    logConfirmationReversed,
    logShipmentAssociated,
    logShipmentCreated,
    formatValue
  }
}
